import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import config from 'config';
import { fullName, formatCurrency } from 'utils';
import Container from 'common/container';
import Icon from 'common/icon';
import GlobalContext from 'site/context';
import ContributeContext from '../context';
import StepTitle from '../step-title';
import PresetTile from '../amount-tile-preset';
import SimpleTile from '../amount-tile-simple';
import CustomTile from '../amount-tile-custom';
import AmountTable from '../amount-table';
import PaymentButtons from '../payment-buttons';
import TipDescriptionModal from './tip-description';

const SelectAmount = ({ onTipChange, onContinue }) => {
    const { campaign, tip, subtotal, total, amount, type } = useContext(ContributeContext);
    const { showFlash } = useContext(GlobalContext);

    // Possible states: inactive, info, entry
    const [modalState, setModalState] = useState('inactive');

    // Alter preset percentages for large contributions
    const presetPercentages = subtotal > 50000 ? [10, 12, 14] : [10, 15, 20];

    // Calculate estimates & remove amounts too small to charge
    const minimum = config('/minimum_tip_charge');
    const presets = presetPercentages
        .map(percent => {
            const estimate = subtotal * (percent / 100);
            const roundedEstimate = Math.round(estimate / 5) * 5;

            return {
                percentage: `${percent}%`,
                amount: roundedEstimate,
            };
        })
        .filter(preset => preset.amount > minimum);

    // Determine name to display in description text
    const ownerName = fullName(campaign.owner.firstName, campaign.owner.lastName);
    const recipientName = campaign.shouldDisplayBenefitInHero
        ? campaign.benefits
        : campaign.recipient.name;
    const displayName = campaign.recipient.type === 'individual' ? ownerName : recipientName;

    // Track whether a custom amount has been entered
    const [isCustom, setIsCustom] = useState(tip && !presets.some(x => x.amount === tip));

    // Flag to indicate if a tip has been selected (even if it's 0)
    const hasTip = tip != null;

    // Default selection to the middle preset
    useEffect(() => {
        if (!hasTip) onTipChange(presets[1].amount);
    }, []);

    const handleContinue = () => {
        if (tip && tip < minimum) {
            showFlash({
                type: 'danger',
                title: 'Minimum Tip!',
                message: 'There is a minimum tip of $0.50 due to credit card processing fees.',
                duration: 5000,
            });
        } else {
            onContinue();
        }
    };

    return (
        <>
            <TipDescriptionModal
                show={modalState !== 'inactive'}
                showEntry={modalState === 'entry'}
                onAmountChange={onTipChange}
                onClose={() => {
                    if (modalState === 'entry') setIsCustom(true);
                    setModalState('inactive');
                }}
            />

            <Container className="pt-8 pb-16 flex-1 flex flex-col justify-start max-w-md">
                <div className="mt-12 rounded-lg lg:bg-white lg:shadow lg:p-16">
                    <StepTitle
                        title={
                            <>
                                Choose tip amount
                                <button
                                    type="button"
                                    className="inline-block text-14 link-green ml-3 align-middle"
                                    onClick={() => setModalState('info')}
                                >
                                    <Icon icon="question-circle" size="lg" />
                                </button>
                            </>
                        }
                    />
                    <p className="text-center text-16 leading-relaxed">
                        Pledge It is proud to be free for{' '}
                        <strong className="font-bold text-gray-80">{displayName}</strong>. We have a
                        0% platform fee and rely on the generosity of donors like you to continue
                        offering our platform for free.
                    </p>

                    <div className="md:px-12">
                        <div className="mt-14 mb-20">
                            <div className="flex flex-wrap md:flex-no-wrap justify-center items-stretch -ml-4">
                                {presets.map(preset => (
                                    <div className="w-1/3 pl-4" key={preset.percentage}>
                                        <PresetTile
                                            amount={preset.percentage}
                                            estimate={formatCurrency(preset.amount)}
                                            active={!isCustom && preset.amount === tip}
                                            onClick={() => {
                                                setIsCustom(false);
                                                onTipChange(preset.amount);
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                            {isCustom ? (
                                <div className="mt-4">
                                    <CustomTile
                                        label="Custom tip amount"
                                        active={isCustom}
                                        onClick={() => {}}
                                        onChange={x => onTipChange(Number(x) * 100)}
                                        value={(tip ?? 0) / 100}
                                    />
                                </div>
                            ) : (
                                <div className="flex flex-wrap md:flex-no-wrap justify-center items-stretch -ml-4 mt-4">
                                    <div className="w-1/2 pl-4">
                                        <SimpleTile
                                            label="No Tip"
                                            active={false}
                                            onClick={() => {
                                                onTipChange(0);
                                                setModalState('entry');
                                            }}
                                        />
                                    </div>
                                    <div className="w-1/2 pl-4">
                                        <SimpleTile
                                            label="Custom"
                                            active={false}
                                            onClick={() => {
                                                if (!tip) onTipChange(presets[1].amount);
                                                setModalState('entry');
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-12 md:mt-16">
                            <AmountTable
                                data={[
                                    {
                                        label: `${formatCurrency(amount)} ${type}`,
                                        amount: formatCurrency(subtotal),
                                    },
                                    {
                                        label: 'Tip',
                                        amount: !hasTip ? '-' : formatCurrency(tip),
                                        muted: !hasTip,
                                    },
                                ]}
                                totalValue={formatCurrency(total)}
                                showTotal
                            />
                        </div>

                        <div className="mt-8 md:mt-14">
                            <PaymentButtons disabled={!hasTip} onContinue={handleContinue} />
                        </div>
                    </div>
                </div>
            </Container>
        </>
    );
};

SelectAmount.propTypes = {
    onTipChange: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
};

export default SelectAmount;
